import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { selectContractDetail } from 'src/store/selectors/contractSelectors';
import { formatAmount } from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import useStyles from './settlementComparisonStyles';

// formatAmount pins both the minimum and the maximum to 2 decimals.
// formatCurrencyWithDecimals leaves minimumFractionDigits at 1, which renders
// an amount without cents as "$98,725.0" — ragged decimals in a dialog whose
// whole job is comparing amounts against each other.
const currency = value => `$${formatAmount(value ?? 0)}`;

/**
 * What the client paid to close the fiscal balance, against what it would have
 * taken to close the accounting balance that same month.
 *
 * The whole gap is opened by the grace period: while the client pays nothing,
 * the fiscal balance does not amortize but the accounting book does. That gap
 * then closes on its own with every amortization, reaching zero at the
 * contract's natural end — so it only ever surfaces on an early payoff, and a
 * contract with no grace period shows no difference at all.
 */
const SettlementComparisonDialog = ({
  open,
  onClose,
  settlement,
  eventDate
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const contract = useSelector(selectContractDetail);

  if (!open || !settlement) {
    return null;
  }

  // Deliberately not showing the contract's `gracePeriod` here: that field is
  // days-to-first-rent, not months of grace (its ZERO_DAYS option renders as
  // "30 días"), and the accounting schedule derives its own grace from
  // firstRentDate against startDate — the two can disagree. Naming a number
  // that does not drive this calculation would be worse than naming none.
  const subtitle = t('CONTRACT_VIEW.EVENTS.SETTLEMENT.SUBTITLE', {
    folio: contract?.folio ?? ''
  });

  return (
    <Dialog
      id="settlement-comparison"
      classes={{ paper: classes.paper }}
      open={open}
      onClose={onClose}
      scroll="body"
      fullWidth
      aria-labelledby="settlement-comparison-title"
    >
      <DialogTitle className={classes.header} component="div">
        <Box>
          <Typography variant="overline" className={classes.eyebrow}>
            {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.EYEBROW')}
          </Typography>
          <Typography
            variant="h3"
            className={classes.title}
            id="settlement-comparison-title"
          >
            {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.TITLE')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label={t('COMMON.CLOSE')}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.summary}>
          <Box className={classes.card}>
            <Typography variant="overline" className={classes.cardLabel}>
              {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.CLOSED_FISCAL')}
            </Typography>
            <Typography variant="h2" className={classes.cardAmount}>
              {currency(settlement.paidAmount)}
            </Typography>
            <Typography variant="caption" className={classes.cardHint}>
              {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.CLOSED_FISCAL_HINT')}
            </Typography>
          </Box>

          <Box className={`${classes.card} ${classes.cardAlt}`}>
            <Typography variant="overline" className={classes.cardLabel}>
              {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.WOULD_CLOSE_ACCOUNTING')}
            </Typography>
            <Typography variant="h2" className={classes.cardAmount}>
              {currency(settlement.accountingBalance)}
            </Typography>
            <Typography variant="caption" className={classes.cardHint}>
              {t(
                'CONTRACT_VIEW.EVENTS.SETTLEMENT.WOULD_CLOSE_ACCOUNTING_HINT'
              )}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.rows}>
          <Box className={classes.row}>
            <Box>
              <Typography variant="body1" className={classes.rowLabel}>
                {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.PAID_AMOUNT')}
              </Typography>
              <Typography variant="caption" className={classes.rowHint}>
                {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.PAID_AMOUNT_DETAIL')}
              </Typography>
            </Box>
            <Typography variant="body1" className={classes.rowValue}>
              {currency(settlement.paidAmount)}
            </Typography>
          </Box>

          <Box className={classes.row}>
            <Box>
              <Typography variant="body1" className={classes.rowLabel}>
                {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.ACCOUNTING_BALANCE')}
              </Typography>
              <Typography variant="caption" className={classes.rowHint}>
                {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.ACCOUNTING_BALANCE_DETAIL')}
              </Typography>
            </Box>
            <Typography variant="body1" className={classes.rowValue}>
              {currency(settlement.accountingBalance)}
            </Typography>
          </Box>

          <Box className={classes.row}>
            <Box>
              <Typography variant="body1" className={classes.rowLabel}>
                {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.DIFFERENCE')}
              </Typography>
              <Typography variant="caption" className={classes.rowHint}>
                {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.DIFFERENCE_DETAIL')}
              </Typography>
            </Box>
            <Typography variant="body1" className={classes.rowValueTotal}>
              {currency(settlement.difference)}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.criteria}>
          <InfoOutlinedIcon fontSize="small" className={classes.criteriaIcon} />
          <Box>
            <Typography variant="body2" className={classes.criteriaTitle}>
              {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.CRITERIA_TITLE')}
            </Typography>
            <Typography variant="body2">
              {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.CRITERIA_BODY')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <Box className={classes.actions}>
        <Typography variant="body2" className={classes.registered}>
          {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.REGISTERED_ON', {
            date: selectFormatDate(eventDate)
          })}
        </Typography>
        <Button variant="contained" color="primary" onClick={onClose}>
          {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.UNDERSTOOD')}
        </Button>
      </Box>
    </Dialog>
  );
};

SettlementComparisonDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  eventDate: PropTypes.string,
  settlement: PropTypes.shape({
    paidAmount: PropTypes.number,
    accountingBalance: PropTypes.number,
    difference: PropTypes.number
  })
};

export default SettlementComparisonDialog;
